import {
  Component,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  inject,
  OnInit,
  signal,
  linkedSignal,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IconBtnComponent } from '@enginuity/core/molecules/icon-btn/icon-btn.component';
import { Subject, fromEvent } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { exportProcessStatus } from '@services/core-services/constants/export-process-status';
import { DownloadsService } from '@services/core-services/downloads.service';
import { NotifyService } from '@services/core-services/notify.service';
import { ErrorHandlerService } from '@services/core-services/error-handler.service';
import { BadgesComponent } from '@enginuity/core/molecules/badges/badges.component';
import { ActionBtnComponent } from '@enginuity/core/molecules/action-btn/action-btn.component';
import { getTimeInterval } from '@services/utils/time.utils';
import { ViewStatus } from '@services/ui-services/models/view-status.enum';
import { CeIconComponent } from '@enginuity/core/atoms/ce-icon/ce-icon.component';
import { HttpErrorResponse } from '@angular/common/http';
import { DownloadsDataStore } from './downloads-data.store';
import { GlobalLoaderWrapperComponent } from '@enginuity/analytics/organisms/global-loader-wrapper/global-loader-wrapper.component';

@Component({
  selector: 'app-downloads-data',
  standalone: true,
  imports: [
    CommonModule,
    BadgesComponent,
    MatTooltipModule,
    IconBtnComponent,
    CeIconComponent,
    ActionBtnComponent,
    GlobalLoaderWrapperComponent,
  ],
  providers: [DownloadsDataStore],
  templateUrl: './downloads-data.component.html',
  styleUrls: ['./downloads-data.component.scss'],
})
export class DownloadsDataComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('downloadsContainer') downloadsContainer!: ElementRef;

  private downloadsService = inject(DownloadsService);
  private notify = inject(NotifyService);
  private errorHandler = inject(ErrorHandlerService);
  private downloadsStore = inject(DownloadsDataStore);

  private currentPage = signal(1);
  private pageLimit = signal(25);
  private hasMorePages = signal(true);
  loaderImagePath: string = '/assets/loader-spin.svg';

  buttonDisabled = signal(false);
  isLoadingMore = signal(false);
  tooltipData = signal('Refresh');

  downloadsResource = this.downloadsStore.createDownloadsResource(this.currentPage, this.pageLimit);

  // LinkedSignal that maintains data even during loading states
  persistentDownloadsData = linkedSignal({
    source: () => this.downloadsResource,
    computation: (currentResource: any, previous?: any) => {
      // If resource has value, use it
      if (currentResource.hasValue()) {
        return currentResource.value();
      }
      // If loading and we have previous data, keep it
      if (currentResource.isLoading() && previous?.value) {
        return previous.value;
      }
      // Otherwise return undefined (will show global loader)
      return undefined;
    },
  });

  private destroy$ = new Subject<void>();

  constructor() {
    // Automatically clear isLoadingMore when resource finishes loading during pagination
    effect(() => {
      const resource = this.downloadsResource;
      const loadingMore = this.isLoadingMore();

      // If we were loading more and resource is no longer loading, clear the flag
      if (loadingMore && !resource.isLoading() && resource.hasValue()) {
        this.isLoadingMore.set(false);
      }
    });
  }

  ngOnInit() {
    this.loadInitialDownloads();
  }

  ngAfterViewInit() {
    this.setupScrollListener();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupScrollListener() {
    const container = this.downloadsContainer?.nativeElement;
    if (!container) return;

    fromEvent(container, 'scroll')
      .pipe(takeUntil(this.destroy$), debounceTime(100))
      .subscribe(() => {
        if (this.isLoadingMore() || !this.hasMorePages()) return;

        const { scrollTop, scrollHeight, clientHeight } = container;
        if (scrollHeight - scrollTop <= clientHeight * 1.5) {
          this.loadMoreDownloads();
        }
      });
  }

  private loadInitialDownloads() {
    this.currentPage.set(1);
    this.hasMorePages.set(true);
    this.downloadsResource.reload();
  }

  private loadMoreDownloads() {
    if (!this.hasMorePages() || this.isLoadingMore()) return;
    const currentValue = this.downloadsResource.value();
    if (!currentValue?.pagination?.next_page) {
      this.hasMorePages.set(false);
      return;
    }

    this.isLoadingMore.set(true);
    this.currentPage.update(page => page + 1);
    this.downloadsResource.reload();
  }

  getStatusBadge(status: exportProcessStatus): string {
    switch (status) {
      case exportProcessStatus.Failed:
        return ViewStatus.Cancelled;
      case exportProcessStatus.Ready:
        return ViewStatus.Live;
      case exportProcessStatus.Pending:
        return ViewStatus.Paused;
      default:
        return 'default';
    }
  }

  refreshDownloads() {
    if (this.buttonDisabled()) return;

    this.buttonDisabled.set(true);
    this.tooltipData.set('Refreshing...');
    this.currentPage.set(1);
    this.hasMorePages.set(true);
    this.persistentDownloadsData.set(undefined);
    this.downloadsResource.set(undefined);
    this.downloadsResource.reload();

    setTimeout(() => {
      this.buttonDisabled.set(false);
      this.tooltipData.set('Refresh');
    }, 1000);
  }

  onDownload(jobId: string) {
    try {
      this.downloadsService.downloadFile(jobId);
    } catch (error) {
      const errorResponse = this.errorHandler.handleServerError(error as HttpErrorResponse);
      this.notify.error(errorResponse.message);
    }
  }

  getTimeInterval(date: string): string {
    return getTimeInterval(date);
  }

  onScroll(event: Event) {
    const element = event.target as HTMLElement;
    if (this.isLoadingMore() || !this.hasMorePages()) return;

    const { scrollTop, scrollHeight, clientHeight } = element;
    if (scrollHeight - scrollTop <= clientHeight * 1.5) {
      this.loadMoreDownloads();
    }
  }

  getErrorMessage() {
    if (this.downloadsResource.error()) {
      const error = this.downloadsResource.error() as HttpErrorResponse;
      const errorResponse = this.errorHandler.handleServerError(error);
      return {
        title: 'Error Loading Downloads',
        message: errorResponse.message,
      };
    }
    return {
      title: '',
      message: '',
    };
  }
}
